import Immutable, { Collection, isCollection, List, Map } from 'immutable'

export function ImmutableMapFactory<T>(obj: T): ImmutableMap<T> {
  return Map(obj as { [key: string]: any }) as ImmutableMap<T>
}

export interface ImmutableMap<T> extends Map<any, any> {
  getIn(keyPath: Iterable<unknown>): any

  getIn(keyPath: Iterable<unknown>, notSetValue?: unknown): any

  get<K extends keyof T>(name: K): T[K]

  get<K extends keyof T>(name: K, notSetValue: T[K]): T[K]

  set<K extends keyof T>(name: K, value: T[K]): this

  merge<K extends keyof T>(...collections: Iterable<[K, T[K]]>[]): Map<K, T[K]>

  merge<K extends keyof T>(...collections: { K: T[K] }[]): Map<K, T[K]>

  update<K extends keyof T>(key: K, notSetValue: T[K], updater: (value: T[K]) => T[K]): this

  update<K extends keyof T>(key: K, updater: (value: T[K]) => T[K]): this

  update<R>(updater: (value: this) => R): R
}

export function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export function isNullOrUndefined<T>(value: T | null | undefined): value is null | undefined {
  return value === null || value === undefined
}

export function isCollectionOf<K, V>(maybeCollection: unknown): maybeCollection is Collection<K, V> {
  return isCollection(maybeCollection)
}

export function chunked<T>(list: List<T>, chunkSize: number): List<List<T>> {
  return Immutable.Range(0, list.count(), chunkSize)
    .map((chunkStart) => list.slice(chunkStart, chunkStart + chunkSize))
    .toList()
}

/**
 * Creates chunks from both lists and returns all combinations (Cartesian product) of these chunks.
 * For example, with first=[1,2,3,4], second=[A,B,C], chunkSize=2:
 * - first chunks: [[1,2], [3,4]]
 * - second chunks: [[A,B], [C]]
 * - result: [
 *     {first: [1,2], second: [A,B]},
 *     {first: [1,2], second: [C]},
 *     {first: [3,4], second: [A,B]},
 *     {first: [3,4], second: [C]}
 *   ]
 */
export function groupChunked<T1, T2>(
  first: List<T1>,
  second: List<T2>,
  chunkSize: number
): List<{ first: List<T1>; second: List<T2> }> {
  const firstChunks = chunked(first, chunkSize)
  const secondChunks = chunked(second, chunkSize)

  return firstChunks
    .flatMap((firstChunk) =>
      secondChunks.map((secondChunk) => ({
        first: firstChunk,
        second: secondChunk
      }))
    )
    .toList()
}
